/**
 * Generador de contorno de trazo a mano alzada con ancho variable por presión.
 * Produce un polígono cerrado (izquierda + derecha invertida) que se rellena en canvas,
 * dando la punta afilada y el trazo "húmedo" de una pluma real (Apple Pencil incluido).
 */

export interface InkPoint {
  x: number
  y: number
  /** 0..1; 0.5 = presión neutra (mouse / dedo) */
  p: number
}

export interface StrokeOptions {
  size: number
  /** 0 = ancho constante (resaltador), 1 = totalmente sensible a la presión */
  thinning?: number
  /** suavizado exponencial de la entrada, 0..1 */
  streamline?: number
  /** afilar el inicio del trazo (solo sin presión real: con lápiz la da la mano) */
  taperStart?: number
  /** afilar el final del trazo (ídem) */
  taperEnd?: number
  /** trazo terminado: habilita el afilado final de los trazos sin presión */
  last?: boolean
  /** ancho constante, sin presión (resaltador) */
  flat?: boolean
}

/*
 * ── Por qué este motor se ve como Notas o Freeform ─────────────────────────
 *
 * 1. La línea central se reconstruye con una spline Catmull-Rom CENTRÍPETA y se
 *    remuestrea a paso fijo (una fracción del grosor). Así da igual si llegan
 *    puntos a 240 Hz separados medio píxel (en vivo) o pocos puntos después de
 *    simplificar (guardado): la curva es la misma y es suave. La variante
 *    centrípeta no hace rizos ni sobrepasos en las esquinas cerradas.
 * 2. La dirección en cada muestra es la bisectriz de los dos tramos vecinos, ya
 *    sobre muestras uniformes: el borde del trazo deja de temblar con el ruido
 *    del digitalizador.
 * 3. Donde el trazo da media vuelta (la "n", la "m", la punta de la "v") se
 *    dibuja una tapa redonda en vez de un pico.
 * 4. La presión se toma TAL CUAL de los puntos. Su suavizado vive en
 *    streamlinePoints, que es lo que se guarda: lo que ves al escribir y lo que
 *    queda guardado salen de los mismos datos con las mismas reglas.
 * 5. Con presión real no hay afilado artificial: la mano ya afila al apoyar y al
 *    levantar el lápiz. Afilarlo encima era el "empieza fino y luego engorda".
 *
 * La app de iPad lleva una traducción exacta a Swift (InkGeometry.swift):
 * cualquier cambio aquí hay que replicarlo allá.
 */

/** Paso del remuestreo de la línea central, como fracción del grosor. */
const SAMPLE_SPACING = 0.25
/**
 * Puntos más cercanos que esto (fracción del grosor) se funden en uno. Igual al
 * paso de muestreo: cuando el lápiz va lento llegan puntos a décimas de píxel
 * y el temblor del digitalizador, más grande que esa separación, fingía
 * "medias vueltas" que dibujaban bultitos en el borde.
 */
const MIN_POINT_DISTANCE = SAMPLE_SPACING
/** Exponente de la curva de presión del lápiz (ver pencilPressure). */
const PRESSURE_EXPONENT = 0.5

function easeOutSine(t: number): number {
  return Math.sin((t * Math.PI) / 2)
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

/**
 * Curva de respuesta del Apple Pencil: de fuerza normalizada (0..1 del máximo
 * del lápiz) a presión de tinta. Al escribir normal se usa ~12-35 % de la
 * fuerza máxima, justo donde una respuesta lineal hace saltar el grosor con
 * cualquier variación mínima. Con la raíz cuadrada la escritura normal cae
 * alrededor de 0.5 —el grosor nominal— y la curva es suave hacia los extremos.
 */
export function pencilPressure(normalizedForce: number): number {
  if (!(normalizedForce > 0)) return 0.5
  return Math.min(1, Math.max(0.08, Math.pow(Math.min(1, normalizedForce), PRESSURE_EXPONENT)))
}

/**
 * Suaviza los puntos de entrada con un filtro exponencial (reduce el temblor de
 * la mano). La presión se suaviza con el mismo filtro: queda horneada en los
 * datos que se guardan, así el trazo guardado y el que se ve en vivo coinciden.
 */
export function streamlinePoints(points: InkPoint[], streamline: number): InkPoint[] {
  if (points.length < 2 || streamline <= 0) return points
  const t = 0.15 + (1 - streamline) * 0.85
  const out: InkPoint[] = [points[0]]
  for (let i = 1; i < points.length; i++) {
    const prev = out[out.length - 1]
    const cur = points[i]
    out.push({
      x: prev.x + (cur.x - prev.x) * t,
      y: prev.y + (cur.y - prev.y) * t,
      p: prev.p + (cur.p - prev.p) * t,
    })
  }
  return out
}

/** ¿Los puntos traen presión real (lápiz) o todos el valor neutro del ratón? */
export function hasPressureVariation(points: InkPoint[]): boolean {
  for (const point of points) {
    if (Math.abs(point.p - 0.5) > 0.02) return true
  }
  return false
}

/**
 * Funde puntos más cercanos que el paso de muestreo del grosor dado. Lo usan el
 * render (la spline no admite tramos de largo cero) y el guardado: así el trazo
 * que queda en el tablero parte de los mismos puntos que el que se vio al
 * escribir, y el temblor de la escritura lenta no se guarda.
 */
export function thinPoints(points: InkPoint[], size: number): InkPoint[] {
  if (points.length < 2) return points
  return dedupePoints(points, Math.max(size * MIN_POINT_DISTANCE, 1e-3))
}

function dedupePoints(points: InkPoint[], minDistance: number): InkPoint[] {
  const out: InkPoint[] = [points[0]]
  // Entre dos puntos guardados, el saltado que más avanzó en la dirección en
  // que venía el trazo. Si el trazo da media vuelta antes del siguiente punto
  // guardado, ese es el vértice de la vuelta y se conserva: si no, la punta de
  // la "n" o de la "v" sale cortada.
  let candidate: InkPoint | null = null
  let best = -Infinity
  let inX = 0
  let inY = 0

  const keep = (point: InkPoint): void => {
    const last = out[out.length - 1]
    const len = Math.hypot(point.x - last.x, point.y - last.y)
    if (len > 1e-9) {
      inX = (point.x - last.x) / len
      inY = (point.y - last.y) / len
    }
    out.push(point)
  }
  const turnsBack = (c: InkPoint, point: InkPoint): boolean => {
    const last = out[out.length - 1]
    return (c.x - last.x) * (point.x - last.x) + (c.y - last.y) * (point.y - last.y) < 0
  }

  for (let i = 1; i < points.length; i++) {
    const last = out[out.length - 1]
    const point = points[i]
    const dx = point.x - last.x
    const dy = point.y - last.y
    const d = Math.hypot(dx, dy)
    const isLast = i === points.length - 1
    if (d >= minDistance || isLast) {
      if (candidate && turnsBack(candidate, point)) keep(candidate)
      // El último punto manda: es donde el lápiz está (o se levantó).
      if (d >= minDistance || out.length === 1) keep(point)
      else out[out.length - 1] = point
      candidate = null
      best = -Infinity
      continue
    }
    const score = inX !== 0 || inY !== 0 ? dx * inX + dy * inY : d
    if (score > best) {
      candidate = point
      best = score
    }
  }
  return out
}

/** Punto de una Catmull-Rom centrípeta entre p1 y p2 (u en 0..1). */
function catmullRom(
  p0: InkPoint,
  p1: InkPoint,
  p2: InkPoint,
  p3: InkPoint,
  u: number,
): { x: number; y: number } {
  const knot = (a: InkPoint, b: InkPoint): number => Math.max(Math.sqrt(Math.hypot(b.x - a.x, b.y - a.y)), 1e-4)
  const t0 = 0
  const t1 = t0 + knot(p0, p1)
  const t2 = t1 + knot(p1, p2)
  const t3 = t2 + knot(p2, p3)
  const t = t1 + (t2 - t1) * u

  const a1x = ((t1 - t) * p0.x + (t - t0) * p1.x) / (t1 - t0)
  const a1y = ((t1 - t) * p0.y + (t - t0) * p1.y) / (t1 - t0)
  const a2x = ((t2 - t) * p1.x + (t - t1) * p2.x) / (t2 - t1)
  const a2y = ((t2 - t) * p1.y + (t - t1) * p2.y) / (t2 - t1)
  const a3x = ((t3 - t) * p2.x + (t - t2) * p3.x) / (t3 - t2)
  const a3y = ((t3 - t) * p2.y + (t - t2) * p3.y) / (t3 - t2)

  const b1x = ((t2 - t) * a1x + (t - t0) * a2x) / (t2 - t0)
  const b1y = ((t2 - t) * a1y + (t - t0) * a2y) / (t2 - t0)
  const b2x = ((t3 - t) * a2x + (t - t1) * a3x) / (t3 - t1)
  const b2y = ((t3 - t) * a2y + (t - t1) * a3y) / (t3 - t1)

  return {
    x: ((t2 - t) * b1x + (t - t1) * b2x) / (t2 - t1),
    y: ((t2 - t) * b1y + (t - t1) * b2y) / (t2 - t1),
  }
}

/**
 * Reconstruye la línea central con una spline y la muestrea a paso fijo. En los
 * extremos se extrapola un punto fantasma para que la curva salga derecha.
 */
export function resampleStroke(points: InkPoint[], step: number): InkPoint[] {
  if (points.length < 2) return points.slice()
  const ghost = (a: InkPoint, b: InkPoint): InkPoint => ({ x: 2 * a.x - b.x, y: 2 * a.y - b.y, p: a.p })
  const out: InkPoint[] = [points[0]]
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]
    const p2 = points[i + 1]
    const p0 = i > 0 ? points[i - 1] : ghost(p1, p2)
    const p3 = i + 2 < points.length ? points[i + 2] : ghost(p2, p1)
    const segments = Math.max(1, Math.ceil(Math.hypot(p2.x - p1.x, p2.y - p1.y) / step))
    for (let k = 1; k <= segments; k++) {
      const u = k / segments
      const at = k === segments ? p2 : catmullRom(p0, p1, p2, p3, u)
      out.push({ x: at.x, y: at.y, p: p1.p + (p2.p - p1.p) * u })
    }
  }
  return out
}

/**
 * Devuelve el contorno del trazo como lista plana [x0,y0, x1,y1, ...], listo
 * para rellenar con outlineToPath.
 */
export function getStrokeOutline(rawPoints: InkPoint[], options: StrokeOptions): number[] {
  const { size, thinning = 0.55, streamline = 0.5, taperStart = 0, taperEnd = 0, last = false, flat = false } =
    options

  if (rawPoints.length === 0) return []
  const radius = size / 2
  const smoothed = streamlinePoints(rawPoints, streamline)
  const points = dedupePoints(smoothed, Math.max(size * MIN_POINT_DISTANCE, 1e-3))

  // Punto único: círculo.
  if (points.length === 1) {
    const r = flat ? radius : radius * (0.6 + points[0].p * 0.4)
    const out: number[] = []
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2
      out.push(points[0].x + Math.cos(a) * r, points[0].y + Math.sin(a) * r)
    }
    return out
  }

  const samples = resampleStroke(points, Math.max(size * SAMPLE_SPACING, 0.2))
  const n = samples.length

  // Tramos entre muestras: dirección unitaria y longitud acumulada.
  const dirX = new Float64Array(n)
  const dirY = new Float64Array(n)
  const lengths = new Float64Array(n)
  let total = 0
  for (let i = 0; i < n - 1; i++) {
    const dx = samples[i + 1].x - samples[i].x
    const dy = samples[i + 1].y - samples[i].y
    const len = Math.hypot(dx, dy)
    total += len
    lengths[i + 1] = total
    if (len > 1e-9) {
      dirX[i] = dx / len
      dirY[i] = dy / len
    } else if (i > 0) {
      dirX[i] = dirX[i - 1]
      dirY[i] = dirY[i - 1]
    }
  }
  dirX[n - 1] = dirX[n - 2]
  dirY[n - 1] = dirY[n - 2]

  // Con presión real la mano ya afila las puntas; sin ella (ratón) se simula.
  const tapered = thinning > 0 && !hasPressureVariation(rawPoints)
  const startTaper = taperStart > 0 ? taperStart : tapered ? Math.min(size * 1.2, total / 3) : 0
  const endTaper = taperEnd > 0 ? taperEnd : tapered ? Math.min(size * 1.2, total / 3) : 0

  const left: number[] = []
  const right: number[] = []

  for (let i = 0; i < n; i++) {
    const point = samples[i]

    let r: number
    if (flat) {
      r = radius
    } else {
      const pressure = Math.min(1, Math.max(0.02, point.p))
      r = radius * (1 - thinning + easeOutSine(pressure) * thinning * 2) * 0.75
      r = Math.max(r, radius * 0.12)
      const dStart = lengths[i]
      const dEnd = total - lengths[i]
      if (startTaper > 0 && dStart < startTaper) r *= easeOutCubic(dStart / startTaper)
      if (last && endTaper > 0 && dEnd < endTaper) r *= easeOutCubic(dEnd / endTaper)
      r = Math.max(r, radius * 0.06)
    }

    // Media vuelta (la "n", la punta de la "v"): tapa redonda en vez de pico.
    // Se exige que el giro se sostenga dos tramos para no confundirlo con ruido.
    if (
      i > 0 &&
      i < n - 2 &&
      dirX[i - 1] * dirX[i] + dirY[i - 1] * dirY[i] < 0 &&
      dirX[i - 1] * dirX[i + 1] + dirY[i - 1] * dirY[i + 1] < 0
    ) {
      pushCornerCap(left, right, point.x, point.y, dirX[i - 1], dirY[i - 1], r)
      continue
    }

    // Dirección: bisectriz de los tramos vecinos (sin temblor en muestras uniformes).
    let vx = i > 0 ? dirX[i - 1] + dirX[i] : dirX[i]
    let vy = i > 0 ? dirY[i - 1] + dirY[i] : dirY[i]
    const len = Math.hypot(vx, vy)
    if (len > 1e-9) {
      vx /= len
      vy /= len
    } else {
      vx = dirX[i]
      vy = dirY[i]
    }
    const nx = -vy
    const ny = vx
    left.push(point.x + nx * r, point.y + ny * r)
    right.push(point.x - nx * r, point.y - ny * r)
  }

  // Tapas redondas en ambos extremos, en vivo y guardado por igual: la punta del
  // trazo es redonda como la del lápiz, y no cambia de forma al soltarlo.
  const outline: number[] = []
  for (let i = 0; i < left.length; i += 2) outline.push(left[i], left[i + 1])
  const end = samples[n - 1]
  pushCap(outline, end.x, end.y, left[left.length - 2], left[left.length - 1], right[right.length - 2], right[right.length - 1], dirX[n - 1], dirY[n - 1])
  for (let i = right.length - 2; i >= 0; i -= 2) outline.push(right[i], right[i + 1])
  const start = samples[0]
  pushCap(outline, start.x, start.y, right[0], right[1], left[0], left[1], -dirX[0], -dirY[0])
  return outline
}

/**
 * Tapa redonda en una media vuelta: cada lado del contorno rodea la punta por
 * su cuenta (uno queda por dentro del relleno, el otro dibuja la curva).
 */
function pushCornerCap(
  left: number[],
  right: number[],
  cx: number,
  cy: number,
  dx: number,
  dy: number,
  r: number,
): void {
  // Normal izquierda de la dirección de llegada.
  const nx = -dy
  const ny = dx
  const steps = 12
  for (let k = 0; k <= steps; k++) {
    const a = (Math.PI * k) / steps
    const cos = Math.cos(a)
    const sin = Math.sin(a)
    // Izquierda: de +n hacia -n pasando por la punta (giro -π).
    left.push(cx + (nx * cos + ny * sin) * r, cy + (ny * cos - nx * sin) * r)
    // Derecha: de -n hacia +n pasando por la punta (giro +π).
    right.push(cx + (ny * sin - nx * cos) * r, cy - (nx * sin + ny * cos) * r)
  }
}

/**
 * Tapa redonda de un extremo: semicírculo de `from` a `to` alrededor del
 * centro que pasa SIEMPRE por el lado de fuera (outX, outY). Con "el camino
 * más corto" el sentido quedaba al azar —los dos puntos están exactamente
 * opuestos— y la mitad de las veces la tapa se metía en el trazo y la punta
 * salía con una mordida en vez de redonda.
 */
function pushCap(
  out: number[],
  cx: number,
  cy: number,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  outX: number,
  outY: number,
): void {
  const r = Math.hypot(fromX - cx, fromY - cy)
  if (r < 1e-4) return
  const a0 = Math.atan2(fromY - cy, fromX - cx)
  const a1 = Math.atan2(toY - cy, toX - cx)
  let delta = a1 - a0
  while (delta <= -Math.PI) delta += Math.PI * 2
  while (delta > Math.PI) delta -= Math.PI * 2
  const mid = a0 + delta / 2
  if (Math.cos(mid) * outX + Math.sin(mid) * outY < 0) delta += delta > 0 ? -Math.PI * 2 : Math.PI * 2
  const steps = Math.max(4, Math.ceil(Math.abs(delta) / 0.3))
  for (let i = 1; i < steps; i++) {
    const a = a0 + (delta * i) / steps
    out.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r)
  }
}

/** Dibuja un polígono plano [x,y,...] como path cerrado con esquinas suavizadas. */
export function outlineToPath(outline: number[]): Path2D {
  const path = new Path2D()
  if (outline.length < 4) return path
  const n = outline.length / 2
  path.moveTo((outline[0] + outline[2]) / 2, (outline[1] + outline[3]) / 2)
  for (let i = 1; i < n; i++) {
    const x0 = outline[i * 2]
    const y0 = outline[i * 2 + 1]
    const j = (i + 1) % n
    const x1 = outline[j * 2]
    const y1 = outline[j * 2 + 1]
    path.quadraticCurveTo(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2)
  }
  path.closePath()
  return path
}

/**
 * Reduce puntos para no guardar trazos enormes (Ramer-Douglas-Peucker), pero
 * mirando **forma y presión a la vez**.
 *
 * Con el criterio clásico —solo la forma— un trazo recto hecho con el Apple
 * Pencil pasaba de 120 puntos a 8, y con ellos se iba toda la variación de
 * presión: en vivo el trazo se veía con su grosor cambiante y al soltarlo
 * quedaba plano. Ahora un punto también se conserva si su presión se aparta
 * de la que se interpolaría entre sus vecinos.
 */
export function simplifyInk(points: InkPoint[], tolerance = 0.35, pressureTolerance = 0.05): InkPoint[] {
  if (points.length < 3) return points
  const keep = new Uint8Array(points.length)
  keep[0] = 1
  keep[points.length - 1] = 1
  const stack: [number, number][] = [[0, points.length - 1]]
  while (stack.length) {
    const [first, last] = stack.pop()!
    if (last - first < 2) continue
    const a = points[first]
    const b = points[last]
    let worst = 0
    let index = -1
    for (let i = first + 1; i < last; i++) {
      const point = points[i]
      const t = projectionT(point.x, point.y, a.x, a.y, b.x, b.y)
      const cx = a.x + (b.x - a.x) * t
      const cy = a.y + (b.y - a.y) * t
      const shapeError = Math.hypot(point.x - cx, point.y - cy) / tolerance
      const expectedPressure = a.p + (b.p - a.p) * t
      const pressureError = Math.abs(point.p - expectedPressure) / pressureTolerance
      // Se queda el punto que más se desvía, sea por forma o por presión.
      const error = Math.max(shapeError, pressureError)
      if (error > worst) {
        worst = error
        index = i
      }
    }
    if (worst > 1 && index > 0) {
      keep[index] = 1
      stack.push([first, index], [index, last])
    }
  }
  const out: InkPoint[] = []
  for (let i = 0; i < points.length; i++) if (keep[i]) out.push(points[i])
  return out
}

/** Posición 0..1 de la proyección de un punto sobre el segmento AB. */
function projectionT(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax
  const dy = by - ay
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return 0
  const t = ((px - ax) * dx + (py - ay) * dy) / lenSq
  return t < 0 ? 0 : t > 1 ? 1 : t
}
